import React from 'react';
import { motion } from 'framer-motion';
import { 
  Clock, 
  DollarSign, 
  Users, 
  Calendar, 
  Smartphone, 
  Award, 
  CheckCircle,
  AlertCircle,
  MessageCircle
} from 'lucide-react';

const GrowthChallenges: React.FC = () => {
  const challenges = [
    {
      icon: <Clock size={24} />,
      problem: "Time Drain",
      description: "You're stuck in the shop doing the actual detail work instead of growing your business and developing your team.",
      solution: "DetailerOS coaching will help you get out of installer mindset and into business operators mindset."
    },
    {
      icon: <MessageCircle size={24} />,
      problem: "Client Communications",
      description: "Struggling with consistent client communication leads to lost bookings, missed follow-ups, and poor customer relationships.",
      solution: "Our mentorship and GRIT SUITE integration ensure no client falls through the cracks, with automated follow-ups that clients love."
    },
    {
      icon: <DollarSign size={24} />,
      problem: "Revenue Ceiling",
      description: "Hitting a growth plateau at $30-50K because you can't efficiently scale operations and client management.",
      solution: "DetailerOS members typically grow revenue by 32% in their first year using our proven strategic advice."
    },
    {
      icon: <Smartphone size={24} />,
      problem: "Marketing Struggles",
      description: "Wasting money on ineffective marketing without clear data on which clients and services are most profitable.",
      solution: "Our coaching provides data-driven insights to help focus your efforts on the most profitable client segments."
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { 
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1.0]
      }
    }
  };

  return (
    <section id="challenges" className="py-20 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Vertical guide line */}
        <motion.div 
          className="absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-accent/30 to-transparent"
          initial={{ scaleY: 0, opacity: 0 }}
          whileInView={{ scaleY: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.5 }}
        />
        
        {/* Circular nodes along the line */}
        {[0.15, 0.38, 0.62, 0.85].map((position, index) => (
          <motion.div 
            key={index}
            className="absolute left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-accent"
            style={{ top: `${position * 100}%` }}
            initial={{ scale: 0, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.5 + (index * 0.2) }}
          />
        ))}
        
        {/* Gradient overlay */}
        <motion.div 
          className="gradient-overlay w-[600px] h-[600px] top-[10%] right-[10%]"
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 0.5, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.5, ease: "easeOut" }}
        />
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        <motion.div 
          className="text-center mb-16 max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <motion.span 
            className="px-3 py-1 rounded-full bg-accent/10 border border-accent/20 text-accent font-semibold text-sm inline-block mb-4"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.8 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Growing Pains
          </motion.span>
          <h2 className="text-3xl md:text-4xl font-extrabold mb-6">Why Detailing Businesses Struggle to Scale</h2>
          <p className="text-white/70 text-lg">
            Running a successful detailing business is hard. Growing it to the next level is even harder.
            DetailerOS provides the coaching, strategies and software specifically built to overcome the obstacles keeping you from reaching your potential.
          </p>
        </motion.div>
        
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto"
        >
          {challenges.map((item, index) => (
            <motion.div 
              key={index}
              variants={itemVariants}
              className="glassmorphic rounded-xl p-6 border border-white/10 hover:border-accent/20 transition-colors relative overflow-hidden"
              whileHover={{ 
                y: -5,
                boxShadow: "0 10px 30px -5px rgba(204, 255, 0, 0.2)",
                transition: { duration: 0.2 }
              }}
            >
              {/* Top indicators */}
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center text-accent">
                  {item.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-bold">{item.problem}</h3>
                </div>
              </div>
              
              {/* Problem description */}
              <div className="mb-4 bg-white/5 p-4 rounded-lg border border-white/10 relative pl-10">
                <AlertCircle size={18} className="absolute left-3 top-4 text-error/80" />
                <p className="text-white/80">{item.description}</p>
              </div>
              
              {/* Solution */}
              <div className="bg-accent/5 p-4 rounded-lg border border-accent/20 relative pl-10">
                <CheckCircle size={18} className="absolute left-3 top-4 text-accent" />
                <p className="text-white/90 font-medium">{item.solution}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
        
        <motion.div 
          className="mt-16 max-w-3xl mx-auto text-center bg-white/5 rounded-xl p-6 md:p-8 border border-white/10"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.5 }}
        >
          <Award className="text-accent w-12 h-12 md:w-16 md:h-16 mx-auto mb-4" />
          <h3 className="text-2xl md:text-3xl font-bold mb-4">Join the DetailerOS Elite</h3>
          <p className="text-white/70 text-lg mb-6">
            Our top-performing detailers are growing 3X faster than the industry average.
            DetailerOS isn't just software or coaching - it's a complete business growth system designed specifically for detailing businesses.
          </p>
          <motion.a 
            href="#pricing" 
            className="btn btn-primary inline-flex items-center gap-2"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.97 }}
            onClick={(e) => {
              e.preventDefault();
              const pricing = document.getElementById('pricing');
              if (pricing) {
                pricing.scrollIntoView({ behavior: 'smooth' });
              }
            }}
          >
            <span>See Pricing Options</span>
            <motion.div
              animate={{ x: [0, 5, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, repeatType: "loop" }}
            >
              →
            </motion.div>
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default GrowthChallenges;